import React from 'react';
import type { FeedbackPanelSummaryCard as CardData } from '../../types/feedbackChat';

/**
 * A whole-panel summary.
 *
 * FACTS ONLY, AND ALL OF THEM COMPUTED SERVER-SIDE. Counts, a date, and the
 * names of markers sitting outside their reference range. No explanation, no
 * cause, no risk statement, no recommendation — and nothing the model wrote.
 *
 * The line is drawn there deliberately: "summarise my biomarkers" is a
 * reasonable question and refusing it outright was unhelpful, but a NARRATIVE
 * summary ("your iron markers suggest…") is clinical interpretation of a panel,
 * which is what the decision engine and clinician review exist to produce.
 * Counting how many results fall outside a generated, drift-guarded reference
 * range is arithmetic the member could do themselves on the results page.
 *
 * Markers with no reference range are counted separately rather than folded
 * into "within range", for the same reason toStatus refuses to call an
 * unbounded range in-range.
 */

interface Props {
  card: CardData;
}

export default function FeedbackPanelSummaryCard({ card }: Props) {
  return (
    <div
      className="rounded-2xl p-4 mt-3"
      style={{ backgroundColor: 'var(--melio-cream-warm)', border: '1px solid var(--melio-border)' }}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <span
          className="text-[10px] uppercase tracking-[0.22em]"
          style={{ color: 'var(--melio-olive-deep)' }}
        >
          Your results
        </span>
        {card.collectedAt && (
          <span className="text-[11px]" style={{ color: 'var(--melio-text-muted)' }}>
            {formatDate(card.collectedAt)}
          </span>
        )}
      </div>

      <div className="flex gap-6 mb-3">
        <Stat value={card.total} label="results" />
        <Stat value={card.inRangeCount} label="within range" tone="olive" />
        <Stat value={card.outOfRangeCount} label="outside range" tone="terracotta" />
      </div>

      {card.outOfRange.length > 0 && (
        <div className="pt-3" style={{ borderTop: '1px solid var(--melio-border)' }}>
          <p className="text-[11px] mb-2" style={{ color: 'var(--melio-text-muted)' }}>
            Outside their reference range
          </p>
          <ul className="flex flex-wrap gap-1.5" style={{ listStyle: 'none', padding: 0 }}>
            {card.outOfRange.map((row) => (
              <li
                key={row.key}
                className="text-[12px] px-2.5 py-1 rounded-full border bg-white"
                style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-ink)' }}
              >
                {row.label}
                <span style={{ color: 'var(--melio-terracotta-deep)' }}>
                  {row.status === 'above_range' ? ' ↑' : ' ↓'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {card.noRangeCount > 0 && (
        <p className="text-[11px] mt-3" style={{ color: 'var(--melio-text-muted)' }}>
          {card.noRangeCount} {card.noRangeCount === 1 ? 'marker has' : 'markers have'} no standard
          reference range, so {card.noRangeCount === 1 ? 'it is' : 'they are'} not counted either way.
        </p>
      )}

      <p className="text-[11px] mt-3 leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
        This is a count, not an assessment. Being outside a reference range is common and is not
        the same as something being wrong. Your quarterly report and your care team are where this
        gets interpreted.
      </p>
    </div>
  );
}

function Stat({ value, label, tone }: { value: number; label: string; tone?: 'olive' | 'terracotta' }) {
  const colour = tone === 'olive'
    ? 'var(--melio-olive-deep)'
    : tone === 'terracotta'
      ? 'var(--melio-terracotta-deep)'
      : 'var(--melio-ink)';

  return (
    <div className="flex flex-col">
      <span
        className="text-2xl leading-none"
        style={{ fontFamily: "var(--melio-display)", color: colour }}
      >
        {value}
      </span>
      <span className="text-[11px] mt-1" style={{ color: 'var(--melio-text-muted)' }}>
        {label}
      </span>
    </div>
  );
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
